<script>
import analyticsService from '@services/analytics-service'
import bannerTracker from '@services/mixpanel/banner-tracker'

// A banner counts as "seen" when at least half of it is in the viewport.
const SEEN_VISIBILITY_THRESHOLD = 0.5

export default {
    name: 'PremiumBanner',

    props: {
        source: {
            type: String,
            required: true,
        },
    },

    computed: {
        isPremium() {
            return this.$store.getters['premium/isPremium']
        },
        showBanner() {
            return !this.isPremium
        },
    },

    watch: {
        // The banner root is v-if'd on showBanner, so the element to observe
        // only exists (post-nextTick) once the banner actually renders.
        showBanner: {
            immediate: true,
            handler(visible) {
                if (visible) this.$nextTick(this._observeSeen)
            },
        },
    },

    beforeDestroy() {
        this._disconnectObserver()
    },

    methods: {
        onGoPremium() {
            analyticsService.premiumUpsellInitiated(this.source)
            bannerTracker.onBannerTapped(this.source)
            this.$router.push({ name: 'premium/view-plans', query: { from: this.source } })
        },

        _observeSeen() {
            if (this._seenTracked || this._observer || !this.showBanner) return

            if (!window.IntersectionObserver) {
                // No observer support: count the render as a seen (LazyLoad precedent)
                this._trackSeen()
                return
            }

            this._observer = new IntersectionObserver(entries => {
                if (entries.some(entry => entry.intersectionRatio >= SEEN_VISIBILITY_THRESHOLD)) {
                    this._trackSeen()
                    this._disconnectObserver()
                }
            }, { threshold: SEEN_VISIBILITY_THRESHOLD })
            this._observer.observe(this.$el)
        },

        _trackSeen() {
            if (this._seenTracked) return
            this._seenTracked = true
            bannerTracker.onBannerSeen(this.source)
        },

        _disconnectObserver() {
            if (this._observer) {
                this._observer.disconnect()
                this._observer = null
            }
        },
    },
}
</script>

<template>
    <div
        v-if="showBanner"
        :class="$style.banner"
    >
        <div :class="$style.content">
            <BaseIcon
                name="premium-headphones"
                :class="$style.icon"
            />
            <span :class="$style.headline">HI-FI AUDIO. NO LIMITS.</span>
        </div>
        <BaseButton
            :modifiers="{ noMargins: true, autoWidth: true }"
            :class="$style.cta"
            button-type="primaryFlat"
            size="hero"
            @click="onGoPremium"
        >Go Premium</BaseButton>
    </div>
</template>

<style lang="scss" module>
@use '@design' as *;

// Brand banner creative — Figma 816:361 (desktop) / 816:426 (mobile). The icon
// size, headline size, banner padding and gradient angle are pixel-specific
// brand values with no matching design token; kept as literals so the banner
// matches the Figma exactly across the mobile / tablet / widescreen tiers.
$banner-glow: rgba($c-jazzberry-jam, 0.57);
$banner-shade: rgba($c-black, 0.57);
$banner-base: $c-grey-tundora;

.banner {
    display: flex;
    flex-direction: column;
    gap: $s-m;
    align-items: center;
    padding: $s-xl;
    background: linear-gradient(97.08deg, $banner-glow 0%, $banner-shade 38%), $banner-base;
    border-radius: $size-radius-l;

    @include tablet {
        flex-direction: row;
        gap: $s-l;
        align-items: center;
        justify-content: space-between;
        padding: $s-xl $s-xxl;
        background: linear-gradient(106.24deg, $banner-glow 0%, $banner-shade 38%), $banner-base;
    }

    @include widescreen {
        padding: 40px 32px;
    }
}

.content {
    display: flex;
    gap: $s-l;
    align-items: center;
}

.icon {
    display: none;
    fill: $c-white;

    @include tablet {
        display: block;
        width: 88px;
        height: 46px;
    }

    @include widescreen {
        width: 115px;
        height: 60px;
    }
}

.headline {
    font-size: 20px;
    font-weight: 900;
    line-height: 1.1;
    color: $c-white;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: -0.6px;

    @include tablet {
        font-size: 28px;
        text-align: left;
        letter-spacing: -0.84px;
    }

    @include widescreen {
        font-size: 37px;
        letter-spacing: -1.11px;
    }
}

// Sizing lives in BaseButton's `hero` variant; only the banner's own flex
// behaviour belongs here.
.cta {
    flex-shrink: 0;
}
</style>
